import { X, AlertTriangle } from 'lucide-react';
import { Button } from '@/features/ui/primitives/button';

interface LeadingZeroWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  formFieldLabel: string;
  crmFieldLabel: string;
  crmFieldType: string;
}

const LeadingZeroWarningModal = ({
  isOpen,
  onClose,
  onConfirm,
  formFieldLabel,
  crmFieldLabel,
  crmFieldType,
}: LeadingZeroWarningModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center text-amber-600">
            <AlertTriangle className="h-6 w-6 mr-2" />
            <h2 className="text-xl font-bold text-slate-900">Data Truncation Warning</h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="mb-6 space-y-4">
          <p className="text-slate-700">
            You are mapping the <span className="font-semibold">"{formFieldLabel}"</span> field to{' '}
            <span className="font-semibold">"{crmFieldLabel}"</span>, which has a numeric type (
            <span className="font-mono text-sm">{crmFieldType}</span>).
          </p>
          <p className="text-slate-600">
            Saving to a numeric field will automatically remove any leading zeros and formatting
            (e.g., dashes in a phone number). This may lead to incorrect data.
          </p>
          <p className="font-medium text-slate-800">
            For fields like Tax ID, SSN, ZIP Code, or Phone, it is highly recommended to map to a{' '}
            <strong>Text</strong> or <strong>Phone</strong> field in your CRM to preserve the
            original formatting.
          </p>
        </div>
        <div className="mt-auto flex justify-end gap-3 pt-4 border-t border-slate-200">
          <Button variant="outline" onClick={onClose}>
            Fix Mapping
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            Ignore Risk & Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LeadingZeroWarningModal;
